// Maps
import { iw_dose_alarm_map } from '../maps/alarm/iw-dose';
import { iw_pool_alarm_map } from 'maps/alarm/iw-pool';
import { iw_motor_pump_alarm_map } from 'maps/alarm/iw-motorPump';
import { iw_salt_alarm_map, iw_salt_add_pump_alarm_map } from 'maps/alarm/iw-salt';
import { iw_sense_alarm_map, iw_sense_out_alarm_map } from 'maps/alarm/iw-sense';
import { models } from '../maps/deviceModels';

// Utils
import { isMultiPool } from './multidevice';
import { checkIsSense } from 'components/devices/IwSense/utils/senseDevices';
import { checkIsSalt } from 'components/devices/Salt/utils/saltDevices';

const alarmMap = {
  DOSE: { alarmMessages: iw_dose_alarm_map },
  MULTIPOOL: { alarmMessages: iw_pool_alarm_map },
  POOL: { alarmMessages: iw_pool_alarm_map },
  SALT: { alarmMessages: iw_salt_alarm_map },
  SALT_ADD_PUMP: { alarmMessages: iw_salt_add_pump_alarm_map },
  SENSE: { alarmMessages: iw_sense_out_alarm_map },
  ULTRAPUMP: { alarmMessages: iw_motor_pump_alarm_map },
};

const STANDBY_STATUS = 'paused';
const NO_STATUS = 'none';
const PROBLEM_STATUS = 'problem';
const RUNNING_STATUS = 'running';
const WAITING_STATUS = 'waiting';

const getAlarmMessages = (modelFamily, isAdditionalPump = false) => {
  const key = modelFamily === 'SALT' && isAdditionalPump ? 'SALT_ADD_PUMP' : modelFamily;
  return alarmMap[key]?.alarmMessages || [];
};

const getMostImportantMessage = (alarmMessages, alarm) => {
  let mostImportantMessage = { level: 0 };
  for (let i = 0; i < alarm?.length; i++) {
    if (alarm[i] === '1' || alarm[i] === '2') {
      const message = alarm[i] === '2' ? alarmMessages[i + 9] : alarmMessages[i + 1];
      if (message?.level > mostImportantMessage?.level) {
        mostImportantMessage = message;
      }
    }
  }
  return mostImportantMessage;
};
/**
 * Method used to get the device status based on the configuration and status data
 * @param {Object} config
 * @param {Object} status
 * @returns {String} status running/warning/paused
 */

export const getDeviceStatus = (
  config,
  status,
  model,
  heartBeat,
  elements,
  isAdditionalPump = false,
) => {
  if (!status) {
    return NO_STATUS;
  }
  if (!heartBeat) {
    return PROBLEM_STATUS;
  }
  if (!isAdditionalPump && config.STANDBY === '1') {
    return STANDBY_STATUS;
  }

  if (isAdditionalPump && status.OUTPUTS?.charAt(0) === '0') {
    return WAITING_STATUS;
  }

  const modelFamily = isMultiPool(model) ? 'MULTIPOOL' : models[model]?.modelFamily;
  if (!modelFamily) {
    return RUNNING_STATUS;
  }

  const alarmMessages = getAlarmMessages(modelFamily, isAdditionalPump);
  let alarms = [];

  if (checkIsSense(model) && elements) {
    elements.forEach((element) => {
      for (let i = 0; i < element.elementsCount; i++) {
        alarms.push(
          status[
            element.type === 'mA'
              ? `ALARMmA${i}`
              : element.type === 'aux'
              ? `ALARM${i + 5}`
              : `ALARM${i}`
          ],
        );
      }
    });
  } else if (checkIsSense(model) && !elements) {
    alarms.push(status);
  } else if (checkIsSalt(model)) {
    if (!isAdditionalPump) {
      const saltAlarmString = [
        status.ALARM0[4] == '1' ? 1 : 0,
        status.ALARM0[12] == '1' ? 1 : 0,
        status.ALARM1[1] == '1' || status.ALARM2[1] === '1' ? 1 : 0,
        status.ALARM0[15] == '1' ? 1 : 0,
        status.ALARM0[1] == '1' ? 1 : 0,
        status.ALARM0[3] == '1' ? 1 : 0,
        status.ALARM0[8] == '1' ? 1 : 0,
        status.ALARM0[10] == '1' || status.ALARM0[13] === '1' ? 1 : 0,
        status.ALARM0[9] == '1' ? 1 : 0,
        status.ALARM0[17] == '1' ? 1 : 0,
        status.ALARM1[2] == '1' || status.ALARM2[2] === '1' ? 1 : 0,
        status.ALARM1[3] == '1' || status.ALARM2[3] === '1' ? 1 : 0,
        status.ALARM0[7] == '1' ? 1 : 0,
        status.ALARM0[6] == '1' ? 1 : 0,
        status.ALARM1[0] == '1' || status.ALARM2[0] === '1' ? 1 : 0,
        status.ALARM0[0] == '1' ? 1 : 0,
        status.ALARM0[14] == '1' ? 1 : 0,
        status.ALARM0[11] == '1' ? 1 : 0,
        status.ALARM0[20] == '1' ? 1 : 0,
      ].join('');
      alarms.push(saltAlarmString);
    } else {
      const saltAdditionalPumpAlarmString = [
        status.ALARM0[4] == '1' ? 1 : 0,
        status.ALARM0[12] == '1' ? 1 : 0,
        status.ALARM1[1] == '1' || status.ALARM2[1] == '1' ? 1 : 0,
        status.ALARM0[5] == '1' ? 1 : 0,
        status.ALARM0[2] == '1' || status.ALARM0[16] == '1' ? 1 : 0,
        status.ALARM1[2] == '1' || status.ALARM2[2] == '1' ? 1 : 0,
        status.ALARM1[3] == '1' || status.ALARM2[3] == '1' ? 1 : 0,
        status.ALARM0[7] == '1' ? 1 : 0,
        status.ALARM0[6] == '1' ? 1 : 0,
        status.ALARM1[0] == '1' || status.ALARM2[0] == '1' ? 1 : 0,
      ].join('');
      alarms.push(saltAdditionalPumpAlarmString);
    }
  } else alarms.push(status.ALARM);

  // Filter alarms to remove unfined or null values
  alarms = alarms.filter((alarm) => alarm !== undefined && alarm !== null);

  // Initialize the most important message with a low level
  let mostImportantMessage = { level: 0 };

  // Iterate over all alarms to find the most important message
  for (const alarm of alarms) {
    let newAlarmMessages;

    if (checkIsSense(model)) newAlarmMessages = iw_sense_alarm_map;
    else newAlarmMessages = alarmMessages;

    const message = getMostImportantMessage(newAlarmMessages, alarm);
    if (message?.level > mostImportantMessage?.level) {
      mostImportantMessage = message;
    }
  }
  if (mostImportantMessage?.level !== 0) {
    return mostImportantMessage.value;
  }

  return RUNNING_STATUS;
};
/**
 * Method used to get the device status based on the configuration and status data
 * @param {Object} config
 * @param {Object} status
 * @returns {String} status running/warning/paused
 */

export const detectDeviceStatus = (status) => {
  switch (status) {
    case 'paused':
      return 'paused';
    case 'Chemical is finished':
    case 'Lack of flow':
    case 'Lack of flow (salt)':
    case 'Measure communication is not responding':
    case 'Non compliant supply voltage':
    case 'OFA - Over feed alarm':
    case 'OFA alarm':
    case 'problem':
    case 'Safety pressure switcher trigger':
      return 'danger';
    case 'Back-wash needed':
    case 'Boost launched':
    case 'Filtration adjustment needed':
    case 'Flow sensor not enabled':
    case 'FW update in progress':
    case 'Ice risk':
    case 'Mainteinance needed':
    case 'Measure above minimum range':
    case 'Measure below minimum range':
    case 'Measure communication is not present':
    case 'Over-dosing alarm (salt)':
    case 'PT100 is not present':
    case 'Under-dosing alarm (salt)':
    case 'Winter mode':
      return 'warning';
    case 'Lack of measure communication':
    case 'Low level':
    case 'Measure at maximum':
    case 'Measure at minimum':
    case 'Out of time slot':
    case 'Over-dosing alarm':
    case 'Overflow 1xN':
    case 'Overflow ppm':
    case 'PTC intervention (temperature probe)':
    case 'Range alarm':
    case 'Under-dosing alarm':
      return 'pending';
    case 'Controller is in stand-by':
    case 'Delay after calibration':
    case 'Inhibit waiting':
    case 'Lack of PT100':
    case 'Measure in calibration':
    case 'Out of programmed time slot':
    case 'Out of programmed time slots':
    case 'Pump in calibration':
    case 'running':
    case 'Salinity low':
    case 'Standby':
    case 'Startup delay':
    case 'Waiting for pH priority fulfillment':
      return 'running';
    case 'waiting':
    case 'warning':
    case 'none':
      return 'none';
  }
};
